import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetManageParticipantsMetricsCountByProgramId,
  useGetParticipantActivitiesList,
  useGetParticipantDetailsById,
  useGetProgramDetailById,
  useGetSessionsAndEventsList,
  useUpdateNextContactDate,
  useUpdateParticipantTabDetailsMutation,
  useUpdateProgramMutation,
} from "../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../api/services/user/userApi";
import {
  InformationBox,
  MuiGrid,
  WarningConfirmationModal,
} from "../../../components";
import { StatusBadgeType } from "../../../components/statusBadge/StatusBadge";
import {
  BE_ERROR_CODES,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
  ROLE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  TIMEOUT_100,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import {
  convertToUserFormat,
  convertToUTCFormat,
  filterEmptyParams,
  getFullName,
  getParsedErrorCode,
  getParsedErrorMessage,
  getValueOrBlank,
  isNotEmpty,
  scrollToTopContainer,
  trimParams,
} from "../../../helpers/utils";
import { usePermissions } from "../../../hooks/usePermissions";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { navigationDataSelector } from "../../../redux/slices/appState/appStateSelector";
import {
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
  toggleToast,
} from "../../../redux/slices/appState/appStateSlice";
import {
  updateOpcoFieldsEditable,
  updateProgramCreatedDate,
  updateProgramStatus,
} from "../../../redux/slices/program/programSlice";
import {
  getBaseParticipantFormData,
  getParticipantFormForEdit,
  getProgramFormValue,
  isDeliveryCountryJapanNetherlands,
  isValidPostalCode,
} from "../../createProgram/helper";
import {
  createProgramParticipantTabDefaultValue,
  createProgramProgramTabDefaultValue,
  ParticipantTabFormKey,
  ParticipantTabFormType,
  ProgramTabFormHookType,
  ProgramTabFormKey,
} from "../../createProgram/modelsAndSchemas/Models";
import {
  ParticipantTabSchema,
  ProgramTabSchema,
} from "../../createProgram/modelsAndSchemas/ValidationSchema";
import {
  activityTimelineSiteCoreKeys,
  programSiteCoreKeys,
} from "../../createProgram/siteCore/SiteCoreKeys";
import { ManageParticipantsInfo } from "../components/manageParticipantsInfo/ManageParticipantsInfo";
import { ParticipantDetailsCardTypes } from "../modelsAndSchemas/Models";
import { manageParticipantHeaderSiteCoreKeys } from "../sitecore/SiteCoreKeys";
import { CPEditParticipant } from "./cpEditParticipant/CPEditParticipant";
import { CPEditParticipantFooter } from "./cpEditParticipantFooter/CPEditParticipantFooter";
import { CPEditParticipantMainWrapper } from "./CPEditParticipantMain.style";

const TAB_INDEX = {
  PROGRAM: 0,
  PARTICIPANT: 1,
  DOCUMENT: 2,
  ACTIVITY_TIMELINE: 3,
  SESSION_EVENTS: 4,
} as const;

export const pageId = PAGE_IDS.COACH_PORTAL_EDIT_PARTICIPANT;

export const CPEditParticipantMain = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const navigationData = useSelector(navigationDataSelector);
  const { hasModulePermission } = usePermissions();

  const { displayToast } = useToast();
  const location = useLocation();
  const tabValue = location.state?.tabValue ?? 0;
  const { id: programId = "" } = useParams();
  const [selectedTabIndex, setSelectedTabIndex] = useState(tabValue);
  const nextTabIndexRef = useRef(tabValue);
  const [showModifyNextContactDateModal, setShowModifyNextContactDateModal] =
    useState(false);
  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);
  const [isMustStartDateDirty, setIsMustStartDateDirty] = useState(false);

  const {
    useFetchLabel,
    fetchLabel,
    commonValidationMessages,
    commonLabels,
    fetchValidationMessage,
    fetchCommonLabel,
  } = useSiteCore();

  const programMethods = useForm<ProgramTabFormHookType>({
    mode: "all",
    defaultValues: createProgramProgramTabDefaultValue,
    resolver: yupResolver(
      ProgramTabSchema(commonValidationMessages, fetchValidationMessage),
    ),
  });

  const participantActivityTimelineMethods = useForm({
    mode: "all",
  });

  const { getValues, setError, clearErrors } =
    participantActivityTimelineMethods;

  const {
    activityTimeline,
    nextContactDateUpdatedMessage,
    nextContactDateMustBeTodayOrAFutureDate,
  } = activityTimelineSiteCoreKeys;

  const getParticipantActivityLabel = useFetchLabel(activityTimeline);

  const {
    refetchWithParams: getParticipantDetailsByProgramId,
    data: participantDetail,
    isPending: isParticipantDetailPending,
    isError: isParticipantDetailError,
    isSuccess: participantDetailsSuccess,
  } = useGetParticipantDetailsById();

  const {
    data: activityTimelineData,
    isPending: isActivityTimelinePending,
    isError: isActivityTimelineError,
    refetchWithParams: getActivityTimelineData,
    isSuccess: isActivityTimelineSuccess,
  } = useGetParticipantActivitiesList();

  const {
    data: participantsMetricsCountData,
    isPending: isParticipantsMetricsCountPending,
    isError: isParticipantsMetricsCountError,
    refetchWithParams: refetchParticipantsMetricsCount,
    isSuccess: participantsMetricsCountSuccess,
  } = useGetManageParticipantsMetricsCountByProgramId();

  const {
    data: sessionsEventList,
    isFetching: isSessionsEventsPending,
    isError: isSessionsEventsError,
    refetchWithParams: getSessionsAndEventsList,
  } = useGetSessionsAndEventsList();

  const {
    mutateAsync: updateNextContactDate,
    isPending: isUpdateNextContactDatePending,
    isError: isUpdateNextContactDateError,
    isSuccess: isUpdateNextContactDateSuccess,
  } = useUpdateNextContactDate();

  const {
    data: programDetail,
    isFetching: isProgramFetching,
    isError: isProgramError,
    isSuccess: isProgramSuccess,
    refetchWithParams: refetchProgramDetail,
  } = useGetProgramDetailById();

  const {
    mutateAsync: getClientRelationshipOwners,
    isPending: clientRelationshipOwnersPending,
    data: clientRelationshipOwnerList,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: updateParticipantApi,
    isPending: isUpdateParticipantPending,
    isError: isUpdateParticipantError,
    isSuccess: isUpdateParticipantSuccess,
    error: updateParticipantError,
  } = useUpdateParticipantTabDetailsMutation();

  const {
    mutateAsync: updateProgramApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateProgramMutation();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const isCMCProgram = useMemo(() => {
    return (
      programDetail?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programDetail?.solutionOfferingSiteCoreKey]);

  const isTMAssessmentProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programDetail?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_LEADER_COACHING;
  }, [programDetail?.tmProgramType]);

  const {
    reset: programReset,
    handleSubmit: programTabSubmit,
    getValues: programGetValues,
    formState: { isDirty: isProgramTabDirty },
  } = programMethods;

  const hasPermissionOutreach = hasModulePermission(
    MODULE_PERMISSIONS.COACH_PORTAL_EDIT_OUTREACH,
  );

  const participantMethods = useForm<ParticipantTabFormType>({
    mode: "all",
    defaultValues: createProgramParticipantTabDefaultValue,
    resolver: yupResolver(
      ParticipantTabSchema(
        {
          ...commonValidationMessages,
          notificationDateErrorMessage: fetchLabel(
            programSiteCoreKeys.outreach,
            programSiteCoreKeys.notificationDateOutreachEligibleDateValidationMessage,
          ),
          invalidEmployeeIdMessage: fetchLabel(
            programSiteCoreKeys.sponsoringEmployer,
            programSiteCoreKeys.employeeIDValidationMessage,
          ),
          alreadyExistsEmailMessage: fetchLabel(
            programSiteCoreKeys.contactAndNotificationPreferences,
            programSiteCoreKeys.emailIDAlreadyExistsMessage,
          ),
          alreadyExistsNumberMessage: fetchLabel(
            programSiteCoreKeys.contactAndNotificationPreferences,
            programSiteCoreKeys.phoneNumberAlreadyExistsMessage,
          ),
          unsubscribeValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.unsubscribedDateValidationMessage,
          ),
          subscribeValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.programEndDateValidationMessage,
          ),
          personalIdValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.personalIdValidationMessage,
          ),
        },
        getValueOrBlank(userCountryDateFormat),
        isCMCProgram,
        !!programDetail?.isEmployeeIdRequired,
        !!programDetail?.isEmployeeBandRequired,
        getValueOrBlank(programDetail?.deliveryCountry),
        isTMAssessmentProgram,
        isTMLeaderCoachingProgram,
        fetchValidationMessage,
        true,
        hasPermissionOutreach,
        isMustStartDateDirty,
      ),
    ),
  });

  const {
    reset: resetParticipant,
    handleSubmit: participantTabSubmit,
    getValues: participantGetValues,
    formState: {
      isDirty: isParticipantTabDirty,
      dirtyFields: participantDirtyFields,
      touchedFields,
      errors: participantErrors,
    },
    setError: setParticipantError,
    trigger,
  } = participantMethods;

  useEffect(() => {
    const isMustStartByDateDirty =
      participantDirtyFields.mustStartByDate ?? false;
    setIsMustStartDateDirty(isMustStartByDateDirty);
  }, [participantDirtyFields.mustStartByDate]);

  useEffect(() => {
    if (isMustStartDateDirty) {
      trigger(ParticipantTabFormKey.mustStartByDate);
    }
  }, [isMustStartDateDirty, trigger]);

  const callParticipantApi = () => {
    if (isParticipantTabDirty) {
      const baseData = getBaseParticipantFormData(
        participantGetValues,
        touchedFields.countryName,
        userCountryDateFormat,
        isCMCProgram,
        isTMLeaderCoachingProgram,
      );
      updateParticipantApi({
        params: trimParams(
          filterEmptyParams(
            getParticipantFormForEdit(
              participantGetValues,
              baseData,
              userCountryDateFormat,
            ),
          ),
        ),
        programId,
      });
    } else {
      navigateToDocumentsTab();
    }
  };

  const navigateToDocumentsTab = () => {
    setSelectedTabIndex(nextTabIndexRef.current);
    scrollToTopContainer(500);
  };

  useEffect(() => {
    if (isUpdateParticipantSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.candidateUpdateSuccessText,
          ),
        }),
      );
      isUpdateParticipantSuccess && resetParticipant(participantGetValues());
      navigateToDocumentsTab();
    }
  }, [isUpdateParticipantSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            programSiteCoreKeys.keyProgramDetails,
            programSiteCoreKeys.programUpdatedSuccessMessage,
          ),
        }),
      );
      programReset(programGetValues());
      setSelectedTabIndex(nextTabIndexRef.current);
      scrollToTopContainer(500);
    }
  }, [isUpdateSuccess]);

  const getCommonErrorMessageAndShowToast = (error: any) => {
    if (error) {
      const parsedErrorMessage = getParsedErrorMessage(error);
      displayToast(parsedErrorMessage, "error");
    }
  };

  const getErrorCodeAndShowToast = () => {
    const apiErrorCode = getParsedErrorCode(updateParticipantError);

    if (apiErrorCode === BE_ERROR_CODES.NEXT_CONTACT_DATE) {
      setParticipantError(ParticipantTabFormKey.nextContactDate, {
        type: "custom",
        message: fetchLabel(
          activityTimelineSiteCoreKeys.activityTimeline,
          activityTimelineSiteCoreKeys.nextContactDateMustBeTodayOrAFutureDate,
        ),
      });
    } else if (apiErrorCode === BE_ERROR_CODES.INVALID_POSTAL_CODE) {
      const errorPostalCodeMessage = fetchLabel(
        programSiteCoreKeys.addressDetails,
        programSiteCoreKeys.postalCodeFormatErrorMessage,
      );
      displayToast(errorPostalCodeMessage, "error");
    } else {
      getCommonErrorMessageAndShowToast(updateParticipantError);
    }
  };

  const handleParticipantTabAPI = () => {
    if (
      !isValidPostalCode(
        participantErrors,
        setParticipantError,
        ParticipantTabFormKey.postalCode,
      )
    ) {
      trigger();
      return;
    }

    participantTabSubmit(callParticipantApi)();
  };

  const callProgramDetailsApi = () => {
    if (isProgramTabDirty) {
      updateProgramApi(
        getProgramFormValue(
          programGetValues,
          programId,
          programDetail,
          isCMCProgram,
          isTMAssessmentProgram,
          isTMLeaderCoachingProgram,
          hasBusinessApprovalField,
        ),
      );
    } else {
      setSelectedTabIndex(nextTabIndexRef.current);
      scrollToTopContainer(500);
    }
  };

  const hasBusinessApprovalField = useMemo(() => {
    return isDeliveryCountryJapanNetherlands(programDetail?.deliveryCountry);
  }, [programDetail?.deliveryCountry]);

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    // Prevent a keyboard/click-initiated tab switch from overwriting the
    // destination of a save that is still in flight.
    if (isApiLoading) {
      return;
    }

    if (
      value < selectedTabIndex ||
      selectedTabIndex === TAB_INDEX.SESSION_EVENTS
    ) {
      nextTabIndexRef.current = value;
      setSelectedTabIndex(value);
    } else {
      handleContinueButtonClick(value);
    }
  };

  const handleContinueButtonClick = (nextIndex: number) => {
    if (isApiLoading) {
      return;
    }
    nextTabIndexRef.current = nextIndex;
    if (selectedTabIndex === TAB_INDEX.PROGRAM) {
      programTabSubmit(callProgramDetailsApi)();
    } else if (selectedTabIndex === TAB_INDEX.PARTICIPANT) {
      handleParticipantTabAPI();
    } else if (selectedTabIndex === TAB_INDEX.DOCUMENT) {
      setSelectedTabIndex(nextTabIndexRef.current);
    } else if (selectedTabIndex === TAB_INDEX.ACTIVITY_TIMELINE) {
      setSelectedTabIndex(nextTabIndexRef.current);
    } else if (selectedTabIndex === TAB_INDEX.SESSION_EVENTS) {
      navigate(`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${programId}`);
    }
  };

  useEffect(() => {
    if (isProgramSuccess && isNotEmpty(programDetail)) {
      const defaultProgramValues = {
        [ProgramTabFormKey.prefix]: getValueOrBlank(programDetail?.prefixKey),
        [ProgramTabFormKey.firstName]: getValueOrBlank(
          programDetail?.firstName,
        ),
        [ProgramTabFormKey.lastName]: getValueOrBlank(programDetail?.lastName),
        [ProgramTabFormKey.programName]: getValueOrBlank(
          programDetail?.programName,
        ),
        [ProgramTabFormKey.site]: getValueOrBlank(programDetail?.site),
        [ProgramTabFormKey.poNumber]: getValueOrBlank(
          programDetail?.purchaseOrderId,
        ),
        [ProgramTabFormKey.clientInformation1]: getValueOrBlank(
          programDetail?.clientInformation1,
        ),
        [ProgramTabFormKey.clientInformation2]: getValueOrBlank(
          programDetail?.clientInformation2,
        ),
        [ProgramTabFormKey.clientInformation3]: getValueOrBlank(
          programDetail?.clientInformation3,
        ),
        [ProgramTabFormKey.dossierNumber]: getValueOrBlank(
          programDetail?.dossierNumber,
        ),
        [ProgramTabFormKey.actualClientId]: getValueOrBlank(
          programDetail?.actualClientId,
        ),
        [ProgramTabFormKey.businessApprovalNumber]: getValueOrBlank(
          programDetail?.businessApprovalNumber,
        ),
        [ProgramTabFormKey.clientRelationShipOwner]: getValueOrBlank(
          programDetail?.clientRelationShipOwnerId,
        ),
        [ProgramTabFormKey.deliveryOffice]: getValueOrBlank(
          programDetail?.deliveryOfficeId,
        ),
        [ProgramTabFormKey.billingContact]: getValueOrBlank(
          programDetail?.billingContactId,
        ),
        [ProgramTabFormKey.hrContact]: getValueOrBlank(
          programDetail?.hrContactId,
        ),
        [ProgramTabFormKey.projectManager]: getValueOrBlank(
          programDetail?.projectManagerId,
        ),
        [ProgramTabFormKey.projectCoordinator]: getValueOrBlank(
          programDetail?.projectCoordinatorId,
        ),
        [ProgramTabFormKey.costCentre]: getValueOrBlank(
          programDetail?.costCenter,
        ),

        [ProgramTabFormKey.salesCreditDetails]:
          programDetail?.salesCreditDetails ?? [],
        [ProgramTabFormKey.deliveryLeadEngagementManagerId]: getValueOrBlank(
          programDetail?.deliveryLeadEngagementManager?.id,
        ),
        [ProgramTabFormKey.additionalHRContactId]: getValueOrBlank(
          programDetail?.additionalHRContact?.id,
        ),
        [ProgramTabFormKey.additionalInvoiceContactId]: getValueOrBlank(
          programDetail?.additionalInvoiceContact?.id,
        ),
        [ProgramTabFormKey.hiringManager]: getValueOrBlank(
          programDetail?.hiringManager,
        ),
        [ProgramTabFormKey.participantManagerId]: getValueOrBlank(
          programDetail?.participantManager?.id,
        ),

        [ProgramTabFormKey.solutionOffering]: getValueOrBlank(
          programDetail?.solutionOffering,
        ),
        [ProgramTabFormKey.programClass]: getValueOrBlank(
          programDetail?.programClass,
        ),
        [ProgramTabFormKey.businessUnit]: getValueOrBlank(
          programDetail?.businessUnit,
        ),

        [ProgramTabFormKey.billingOffice]: getValueOrBlank(
          programDetail?.billingOffice,
        ),
        [ProgramTabFormKey.salesOffice]: getValueOrBlank(
          programDetail?.salesOffice,
        ),
      };

      programReset({
        ...defaultProgramValues,
      });
      dispatch(
        updateProgramStatus(getValueOrBlank(programDetail?.programStatus)),
      );

      dispatch(updateProgramCreatedDate(programDetail?.createdDate));
      dispatch(
        updateOpcoFieldsEditable(programDetail?.areOpcoFieldsEditable ?? true),
      );
    }
  }, [programDetail, isProgramSuccess]);

  const showUpdatedNextContactDateToast = () => {
    displayToast(
      getParticipantActivityLabel(nextContactDateUpdatedMessage),
      "success",
    );
    closeModifyNextContactDateModal();
  };

  useEffect(() => {
    if (isUpdateNextContactDateSuccess) {
      showUpdatedNextContactDateToast();
      getParticipantDetailsByProgramId(programId);
    }
  }, [isUpdateNextContactDateSuccess]);

  useEffect(() => {
    if (isUpdateNextContactDatePending) {
      dispatch(
        toggleLoader({
          nextContactDateUpdateLoading: {
            isVisible: true,
          },
        }),
      );
    }
    return () => {
      dispatch(
        toggleLoader({ nextContactDateUpdateLoading: { isVisible: false } }),
      );
    };
  }, [isUpdateNextContactDatePending]);

  const isApiLoading =
    isParticipantDetailPending ||
    isActivityTimelinePending ||
    isParticipantsMetricsCountPending ||
    isSessionsEventsPending ||
    isUpdateNextContactDatePending ||
    isProgramFetching ||
    clientRelationshipOwnersPending ||
    isUpdateParticipantPending ||
    isUpdatePending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isApiLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isApiLoading]);

  useEffect(() => {
    if (isUpdateParticipantError) {
      getErrorCodeAndShowToast();
    } else if (
      isParticipantDetailError ||
      isActivityTimelineError ||
      isParticipantsMetricsCountError ||
      isSessionsEventsError ||
      isUpdateNextContactDateError ||
      isProgramError ||
      isUpdateError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isParticipantDetailError,
    isActivityTimelineError,
    isParticipantsMetricsCountError,
    isSessionsEventsError,
    isUpdateNextContactDateError,
    isProgramError,
    isUpdateParticipantError,
    isUpdateError,
  ]);

  useEffect(() => {
    if (programId) {
      getParticipantDetailsByProgramId(programId);
      getActivityTimelineData({ id: programId });
      refetchParticipantsMetricsCount(programId);
      getSessionsAndEventsList(programId);
      refetchProgramDetail(programId);
      getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
    }
  }, [programId]);

  const handleCardClick = (type: ParticipantDetailsCardTypes) => {
    if (type === "activities" || type === "assessments") {
      setSelectedTabIndex(TAB_INDEX.ACTIVITY_TIMELINE);
    } else if (type === "events") {
      setSelectedTabIndex(TAB_INDEX.SESSION_EVENTS);
    }
  };

  const getParticipantName = () => {
    return getFullName(
      participantDetail?.firstName,
      participantDetail?.lastName,
    );
  };

  const participantDetails = useMemo(() => {
    if (
      isActivityTimelineSuccess &&
      isProgramSuccess &&
      participantDetailsSuccess &&
      participantsMetricsCountSuccess &&
      programDetail
    )
      return {
        activitiesCount: activityTimelineData?.length ?? 0,
        clientName: getValueOrBlank(programDetail?.clientName),
        clientCode: getValueOrBlank(programDetail?.clientCode),
        participantName: getParticipantName(),
        assessmentCount: participantsMetricsCountData.assessmentCount,
        sessionAndEventsCount:
          participantsMetricsCountData.completedSessionsAndEventsCount,
        programStatusId: programDetail.programStatus as StatusBadgeType,
        nextContactDate: getValueOrBlank(
          convertToUserFormat(
            participantDetail?.nextContactDate,
            userCountryDateFormat,
          ),
        ),
      };
  }, [
    isActivityTimelineSuccess,
    isProgramSuccess,
    participantDetailsSuccess,
    participantsMetricsCountSuccess,
    programDetail,
    activityTimelineData,
    participantDetail,
    participantsMetricsCountData,
    userCountryDateFormat,
  ]);

  const closeModifyNextContactDateModal = () => {
    clearErrors(ParticipantTabFormKey.nextContactDate);
    setShowModifyNextContactDateModal(false);
  };

  const isValidDate = (): boolean => {
    const dateValue = getValues(ParticipantTabFormKey.nextContactDate);
    const dateValueObj = dayjs(dateValue, userCountryDateFormat);
    if (dateValueObj.isBefore(dayjs(), "day")) return false;
    return true;
  };

  const handleUpdateModifyNextContactDateClick = () => {
    if (!isValidDate()) {
      setError(ParticipantTabFormKey.nextContactDate, {
        type: "custom",
        message: getParticipantActivityLabel(
          nextContactDateMustBeTodayOrAFutureDate,
        ),
      });
      return;
    }
    updateNextContactDate({
      programId: getValueOrBlank(programId),
      nextContactDate: convertToUTCFormat(
        getValues(ParticipantTabFormKey.nextContactDate),
        userCountryDateFormat,
      ),
    });
  };

  const handleBackNavigation = () => {
    location.state?.from === PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT
      ? navigate(-1)
      : navigate(PAGE_URLS.COACH_PORTAL_SEARCH_PARTICIPANTS);
  };

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);
  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    dispatch(setIsDataModifiedCurrentPage(false));

    setTimeout(() => {
      navigate(navigationData.navigationPath);
    }, TIMEOUT_100);
    setShowExitConfirmationModal(false);
  };

  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };
  return (
    <CPEditParticipantMainWrapper data-testid="cp-edit-participant-main-wrapper">
      <MuiGrid container>
        <MuiGrid size={{ sm: 6, xs: 12 }} className="header-left">
          <InformationBox
            title={fetchLabel(
              manageParticipantHeaderSiteCoreKeys.manageParticipantHeaderTitle,
              manageParticipantHeaderSiteCoreKeys.editParticipantHeader,
            )}
          />
        </MuiGrid>
      </MuiGrid>
      <ManageParticipantsInfo
        participantDetails={participantDetails}
        onCardClick={handleCardClick}
        data-testid="cp-edit-participant-info"
      />
      <CPEditParticipant
        programDetail={programDetail}
        participantDetail={participantDetail}
        sessionsEventList={sessionsEventList}
        selectedTabIndex={selectedTabIndex}
        handleUpdateModifyNextContactDateClick={
          handleUpdateModifyNextContactDateClick
        }
        showModifyNextContactDateModal={showModifyNextContactDateModal}
        setShowModifyNextContactDateModal={setShowModifyNextContactDateModal}
        clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
        participantActivityTimelineMethods={participantActivityTimelineMethods}
        participantMethods={participantMethods}
        programMethods={programMethods}
        onTabChange={onTabChange}
      />

      <CPEditParticipantFooter
        pageId={pageId}
        handleNavigateBack={handleBackNavigation}
        onConfirmClick={() => handleContinueButtonClick(selectedTabIndex + 1)}
        confirmationTitle={commonLabels.warning}
        cancelButtonText={commonLabels.close}
        cancelMessage={fetchCommonLabel(
          commonSiteCoreKeys.closeButtonActionText,
        )}
      />

      {showExitConfirmationModal && (
        <WarningConfirmationModal
          labelContent={commonLabels.leavingPageActionText}
          show={showExitConfirmationModal}
          onClose={handleExitConfirmationModalClose}
          onPrimaryButtonClick={handleExitConfirmationModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </CPEditParticipantMainWrapper>
  );
};
